/**
 * ManualVisitContext — carries pre-flight data for a manual entry visit through
 * the canonical StoreDetail workflow. Lets the new consolidated path reuse the
 * exact same UI as a live visit (#1863).
 *
 * Lifecycle:
 *   1. ManualEntryPreflight collects data, calls the server to create the
 *      CallLog with manual stcalltime + drive data, then calls
 *      setManualVisit(...) with the full context + callLogId.
 *   2. setManualVisit persists to sessionStorage under MANUAL_VISIT_KEY so a
 *      refresh mid-flow doesn't strand the rep.
 *   3. useVisitClock reads the context to return injected timestamps for
 *      check-in / check-out events; if no context is set, returns new Date().
 *   4. On checkout completion (or cancellation), call clearManualVisit() to
 *      drop the context + sessionStorage entry.
 *
 * Coexistence with the old manual entry path:
 *   The legacy ManualCallEntry → StoreDetail URL-param flow is untouched. The
 *   old `isManualEntry` / `isFullManualEntry` branches in StoreDetail.tsx only
 *   fire when `manualDate` / `manualEntry` query params are present, and the
 *   new pre-flight never sets those. So the two paths coexist cleanly until
 *   Phase 3 (#1865) deletes the old.
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export type ManualUseCase = 'live-takeover' | 'retroactive';

/** Per-task timing slot inside a manual visit. The first slot's timeIn is the
 * visit time-in; the last slot's timeOut is the visit time-out. */
export interface ManualTaskSlot {
  taskType: 'set' | 'call';
  taskId: number;
  projectId: number;
  projectDescription?: string;
  startTime: string; // ISO timestamp
  endTime: string;   // ISO timestamp
}

export interface ManualVisitData {
  /** When this visit happened. Past date for retroactive, today for live-takeover. */
  date: string; // YYYY-MM-DD

  /** Why the rep is entering this manually. Drives banner copy + GPS treatment. */
  useCase: ManualUseCase;

  /** Visit time block in ISO timestamps. */
  timeIn: string;
  timeOut: string;

  /** Drive segment to the store. */
  driveStartTime: string;  // ISO
  driveEndTime: string;    // ISO  (== timeIn)
  driveStartOdometer: number;
  driveEndOdometer: number;

  /** Store + project context. */
  storeId: number;          // PK
  legacyStoreId: number;
  storeName: string;
  projectId: number;
  projectDescription?: string;
  distributorId?: number;

  /** Optional flags. */
  teamProject?: boolean;
  teamLeader?: boolean;
  notes?: string;

  /** Optional override reason. The GPSCheckIn component in StoreDetail will
   * also re-prompt at "Confirm Arrival" if live GPS is out of range; this
   * carries any reason the rep already provided in pre-flight. */
  gpsOverrideReason?: string;

  /** Per-task timing if the rep recorded multiple tasks in this visit. For a
   * single-task visit, leave undefined and the workflow uses (timeIn, timeOut)
   * for the one task. */
  tasks?: ManualTaskSlot[];

  /** Server CallLog id, stamped after the queued create op syncs. Null until
   * then (the visit is identified locally by visitLocalId in the meantime). */
  callLogId: number | null;

  /** Local visit UUID (the visit_task_sessions uuid) minted by the queued
   * createManualVisit op. StoreDetail adopts this so the visit's dependent
   * writes resolve the server calllogid lazily once it syncs (#2013). */
  visitLocalId?: string;
}

interface ManualVisitContextType {
  manualVisit: ManualVisitData | null;
  setManualVisit: (data: ManualVisitData) => void;
  clearManualVisit: () => void;
}

const ManualVisitContext = createContext<ManualVisitContextType | null>(null);

const SESSION_KEY = 'manual_visit_in_flight';

/** Persisted across page refreshes via sessionStorage (browser-tab lifetime). */
function readSession(): ManualVisitData | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Light shape check — if the persisted value is from an older schema we
    // discard rather than crash StoreDetail downstream.
    if (
      typeof parsed?.date === 'string' &&
      typeof parsed?.timeIn === 'string' &&
      typeof parsed?.timeOut === 'string' &&
      typeof parsed?.storeId === 'number' &&
      (typeof parsed?.callLogId === 'number' || typeof parsed?.visitLocalId === 'string')
    ) {
      return parsed as ManualVisitData;
    }
    return null;
  } catch {
    return null;
  }
}

function writeSession(data: ManualVisitData | null): void {
  try {
    if (data === null) {
      sessionStorage.removeItem(SESSION_KEY);
    } else {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    }
  } catch {
    // Quota exceeded / disabled. The in-memory state will still drive the
    // current tab; only refresh resilience is lost.
  }
}

export function ManualVisitProvider({ children }: { children: ReactNode }) {
  const [manualVisit, setManualVisitState] = useState<ManualVisitData | null>(() => readSession());

  // Sync across tabs if the same rep opens a second tab during a manual flow.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_KEY) {
        setManualVisitState(readSession());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setManualVisit = useCallback((data: ManualVisitData) => {
    writeSession(data);
    setManualVisitState(data);
  }, []);

  const clearManualVisit = useCallback(() => {
    writeSession(null);
    setManualVisitState(null);
  }, []);

  return (
    <ManualVisitContext.Provider value={{ manualVisit, setManualVisit, clearManualVisit }}>
      {children}
    </ManualVisitContext.Provider>
  );
}

export function useManualVisit(): ManualVisitContextType {
  const ctx = useContext(ManualVisitContext);
  if (!ctx) {
    throw new Error('useManualVisit must be used within a ManualVisitProvider');
  }
  return ctx;
}

/** Convenience read-only accessor for code that should be a no-op when no
 * manual visit is active. Returns null instead of throwing. */
export function useManualVisitData(): ManualVisitData | null {
  const ctx = useContext(ManualVisitContext);
  return ctx?.manualVisit ?? null;
}
